import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AiSignals.css';

const scoreColor = (score) => {
  if (score >= 8) return '#2e7d32';
  if (score >= 5) return '#f57c00';
  return '#c62828';
};

const scoreLabel = (score) => {
  if (score >= 9) return 'Fort catalyseur';
  if (score >= 7) return 'Bon potentiel';
  if (score >= 5) return 'Modéré';
  return 'Spéculatif';
};

const AiSignals = () => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const pollRef = useRef(null);

  const { token } = useAuth();

  const headers = useCallback(() =>
    token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  const loadSignals = useCallback(async (d) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/alpaca/ai-signals?date=${d}`, { headers: headers() });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      if (data.success) {
        const sorted = [...(data.data || [])].sort((a, b) => (b.score || 0) - (a.score || 0));
        setSignals(sorted);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  // Polling du statut pendant l'analyse
  const startPolling = useCallback((d) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch('/api/alpaca/ai-signals/status', { headers: headers() });
        const data = await res.json();
        if (!data.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunning(false);
          setStatusMsg('✅ Analyse terminée !');
          await loadSignals(d);
        }
      } catch (e) {
        // ignorer les erreurs de polling
      }
    }, 15000); // poll toutes les 15s
  }, [headers, loadSignals]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    loadSignals(date);
  }, [date, loadSignals]);

  // Vérifier au chargement si une analyse est déjà en cours
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch('/api/alpaca/ai-signals/status', { headers: headers() });
        const data = await res.json();
        if (data.running) {
          setRunning(true);
          setStatusMsg('🔄 Analyse en cours… (résultats dans ~10 min)');
          startPolling(date);
        }
      } catch (e) {}
    };
    if (token) checkStatus();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRunAnalysis = async () => {
    try {
      setRunning(true);
      setStatusMsg('🚀 Lancement de l\'analyse…');
      setError(null);
      const res = await fetch('/api/alpaca/ai-signals/run', {
        method: 'POST',
        headers: headers(),
      });
      const data = await res.json();
      if (data.success) {
        setStatusMsg('🔄 Analyse en cours… les résultats apparaîtront automatiquement (~10 min)');
        startPolling(date);
      } else {
        setRunning(false);
        setStatusMsg(`❌ Erreur: ${data.message}`);
      }
    } catch (e) {
      setRunning(false);
      setStatusMsg(`❌ ${e.message}`);
    }
  };

  return (
    <div className="ai-signals-page">
      <div className="ai-signals-header">
        <div>
          <h1 className="ai-signals-title">Signaux IA</h1>
          <p className="ai-signals-subtitle">
            Recommandations générées par Mistral-Nemo via analyse des actualités Finnhub
          </p>
        </div>
        <button
          className="run-analysis-btn"
          onClick={handleRunAnalysis}
          disabled={running}
        >
          {running ? (
            <><span className="btn-spinner" /> Analyse en cours…</>
          ) : (
            '🔍 Lancer une analyse'
          )}
        </button>
      </div>

      <div className="ai-signals-controls">
        <label>
          Date :
          <input
            type="date"
            value={date}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
            className="date-picker"
          />
        </label>
        <button className="refresh-btn" onClick={() => loadSignals(date)}>↺ Rafraîchir</button>
      </div>

      {statusMsg && (
        <div className={`run-result ${statusMsg.startsWith('✅') ? 'success' : statusMsg.startsWith('❌') ? 'error' : 'info'}`}>
          {statusMsg}
        </div>
      )}
      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Chargement…</p>
        </div>
      ) : signals.length === 0 ? (
        <div className="empty-message">
          <p>Aucun signal IA pour le {date}.</p>
          {!running && <p>Lancez une analyse ou attendez le cron automatique (16h / 18h30 / 20h30 en semaine).</p>}
          {running && <p>L'analyse est en cours, les résultats s'afficheront automatiquement…</p>}
        </div>
      ) : (
        <>
          <div className="signals-summary">
            {signals.length} signal(s) — triés par score de conviction
          </div>
          <div className="signals-grid">
            {signals.map((s) => (
              <div key={s.id} className="signal-card">
                <div className="signal-top">
                  <span className="signal-symbol">{s.symbol}</span>
                  <span className="signal-score" style={{ background: scoreColor(s.score || 0) }}>
                    {s.score || '?'}/10
                  </span>
                </div>
                <div className="signal-label" style={{ color: scoreColor(s.score || 0) }}>
                  {scoreLabel(s.score || 0)}
                </div>
                <div className="signal-action">
                  <span className={`action-badge action-${s.action}`}>{(s.action || 'buy').toUpperCase()}</span>
                  <span className="sentiment-badge">{s.sentiment || 'bullish'}</span>
                </div>
                <p className="signal-reason">{s.reason || '—'}</p>
                <div className="signal-footer">
                  <span className="signal-source">{s.source || 'mistral-nemo'}</span>
                  <span className="signal-priority">#{s.priority || '—'}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AiSignals;
